import { useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle,
  CalendarCheck2,
  ChevronRight,
  Info,
  Link2,
  Loader2,
  MessageSquareText,
  Trash2,
  X,
} from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Switch } from '../../ui/Switch/Switch'
import { usePatientDialog } from '../patients/use-patient-dialog'
import type { ServiceBookingSettings } from './provider-resource-contracts'
import type {
  InformationalService,
  UnifiedServiceRow,
} from './ServicesSettings'
import panel from './SettingsSection.module.css'
import styles from './ServicesSettings.module.css'

type AppointmentType = NonNullable<
  ServiceBookingSettings['configuration']
>['appointmentTypes'][number]

export type ServiceEditActions = {
  rename: (name: string) => void
  setAiAnswers: (checked: boolean) => void
  setBooking: (checked: boolean) => void
  associate: (serviceId: string) => void
  setAppointmentType: (appointmentTypeId: number) => void
  setPatientType: (policy: 'NEW' | 'EXISTING' | 'BOTH') => void
  setAgeRestriction: (checked: boolean) => void
  setAgeBound: (
    bound: 'minimumYears' | 'maximumYears',
    value: number | ''
  ) => void
  setEmergency: (checked: boolean) => void
  setGroup: (group: string) => void
}

/** Removing the service from Flio, offered only for a saved service. */
export type ServiceRemoval = {
  /** The saved service's name: what removal acts on. */
  label: string
  /** Why Remove is unavailable right now, shown beside the button. */
  blockedReason?: string
  pending: boolean
  error?: string
  /** What removal changes for this service, in plain words. */
  consequences: string[]
  onRemove: () => void
}

export type ServiceEditErrors = {
  name?: string
  age?: string
  group?: string
}

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`
}

/**
 * One service's full configuration. Edits go straight into the page's draft
 * (so every cross-service rule still applies), Cancel restores the draft as it
 * was when the dialog opened, and nothing is sent until Save services.
 */
export function ServiceEditDialog({
  row,
  isNew,
  changed,
  appointmentTypes,
  eligibleTypeCount,
  associationOptions,
  savedBooking,
  emergencyReplaces,
  errors,
  actions,
  removal,
  keepOn = {},
  onApply,
  onCancel,
}: {
  row: UnifiedServiceRow
  isNew: boolean
  changed: boolean
  appointmentTypes: AppointmentType[]
  eligibleTypeCount: number
  associationOptions: InformationalService[]
  /** Whether Flio booking is on for this service in the saved settings. */
  savedBooking: boolean
  /** Services that lose the Emergency designation to this one. */
  emergencyReplaces: string[]
  errors: ServiceEditErrors
  actions: ServiceEditActions
  removal?: ServiceRemoval
  /**
   * Why this service's AI answers or Flio booking can't be switched off: it
   * is the last one, and the clinic can't run without one.
   */
  keepOn?: { aiAnswers?: string; booking?: string }
  onApply: () => void
  onCancel: () => void
}) {
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  const baseId = useId()
  const removeTitleId = useId()
  // Editing, or asking about discarding edits or removing the service.
  const [mode, setMode] = useState<'edit' | 'discard' | 'remove'>('edit')
  const safeChoice = useRef<HTMLButtonElement>(null)
  const removing = removal?.pending === true

  function ask(next: 'discard' | 'remove') {
    setMode(next)
    // After the question renders, its safe choice takes focus.
    window.setTimeout(() => safeChoice.current?.focus(), 0)
  }

  function requestClose() {
    if (removing) return
    if (!changed) {
      onCancel()
      return
    }
    ask('discard')
  }

  usePatientDialog(dialog, {
    onClose: () => (mode === 'edit' ? requestClose() : setMode('edit')),
    locked: removing,
  })

  const label = row.informational?.name ?? row.booking?.serviceLabel ?? ''
  const booking = row.booking && !row.booking.disabled ? row.booking : undefined
  const appointmentType = booking
    ? appointmentTypes.find((item) => item.id === booking.appointmentTypeId)
    : undefined
  const ids = {
    name: `${baseId}-name`,
    nameHint: `${baseId}-name-hint`,
    nameError: `${baseId}-name-error`,
    typeHint: `${baseId}-type-hint`,
    typeError: `${baseId}-type-error`,
    ageError: `${baseId}-age-error`,
    groupHint: `${baseId}-group-hint`,
    groupError: `${baseId}-group-error`,
  }
  const blocked = Boolean(errors.name || errors.age || errors.group)
  const bookingToggleDisabled = !row.booking && eligibleTypeCount === 0

  return createPortal(
    <div className={panel.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${panel.dialog} ${styles.editDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className={panel.dialogHeader}>
          <div>
            <span className={panel.eyebrow}>
              {isNew ? 'New service' : 'Service settings'}
            </span>
            <h2 id={titleId}>
              {isNew ? 'Add service' : `Edit ${label.trim() || 'service'}`}
            </h2>
          </div>
          <button
            type="button"
            className={panel.closeButton}
            aria-label="Close"
            disabled={removing}
            onClick={requestClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {mode === 'remove' && removal ? (
          <div className={panel.dialogBody}>
            <div
              className={styles.removePanel}
              role="group"
              aria-labelledby={removeTitleId}
            >
              <span className={styles.removeIcon} aria-hidden="true">
                <Trash2 size={18} />
              </span>
              <div>
                <h3 id={removeTitleId}>Remove {removal.label} from Flio?</h3>
                <ul>
                  {removal.consequences.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {removal.error ? (
                  <p className={panel.fieldError} role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    {removal.error}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className={panel.dialogBody}>
            <div className={panel.field}>
              <label htmlFor={ids.name} className={panel.fieldLabel}>
                Service name
              </label>
              <input
                id={ids.name}
                data-autofocus
                value={label}
                maxLength={row.informational ? 160 : 120}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={`${ids.nameHint}${errors.name ? ` ${ids.nameError}` : ''}`}
                onChange={(event) => actions.rename(event.currentTarget.value)}
              />
              <small id={ids.nameHint} className={panel.hint}>
                Shown to patients and used by Flio’s AI.
              </small>
              {errors.name ? (
                <small
                  id={ids.nameError}
                  className={panel.fieldError}
                  role="alert"
                >
                  {errors.name}
                </small>
              ) : null}
            </div>

            <fieldset className={styles.capabilities}>
              <legend className={styles.groupTitle}>How Flio uses it</legend>
              <label className={styles.capability}>
                <span className={styles.capabilityIcon} aria-hidden="true">
                  <MessageSquareText size={16} />
                </span>
                <span className={styles.capabilityText}>
                  <strong>Available for AI answers</strong>
                  <small>
                    {keepOn.aiAnswers ??
                      'Flio can describe this service on calls and texts.'}
                  </small>
                </span>
                <Switch
                  aria-label="Available for AI answers"
                  checked={row.informational?.availability ?? false}
                  disabled={Boolean(keepOn.aiAnswers)}
                  onChange={(event) =>
                    actions.setAiAnswers(event.currentTarget.checked)
                  }
                />
              </label>
              <label className={styles.capability}>
                <span className={styles.capabilityIcon} aria-hidden="true">
                  <CalendarCheck2 size={16} />
                </span>
                <span className={styles.capabilityText}>
                  <strong>Available for Flio booking</strong>
                  <small>
                    {bookingToggleDisabled
                      ? 'No NexHealth appointment type is ready for Flio booking yet.'
                      : (keepOn.booking ??
                        'Flio books it in NexHealth with the settings below.')}
                  </small>
                </span>
                <Switch
                  aria-label="Available for Flio booking"
                  checked={Boolean(booking)}
                  disabled={bookingToggleDisabled || Boolean(keepOn.booking)}
                  onChange={(event) =>
                    actions.setBooking(event.currentTarget.checked)
                  }
                />
              </label>
            </fieldset>

            {!booking && savedBooking ? (
              <p className={styles.dialogNotice} data-tone="info">
                <Info size={16} aria-hidden="true" />
                Flio stops booking this service when you save. Its booking
                settings are kept until then, so turning booking back on
                restores them.
              </p>
            ) : null}

            {booking ? (
              <fieldset className={styles.bookingGroup}>
                <legend className={styles.groupTitle}>Booking</legend>

                {row.linkageMissing ? (
                  <label className={panel.field}>
                    <span>
                      <Link2 size={14} aria-hidden="true" /> Matching catalog
                      service
                    </span>
                    <select
                      aria-label="Informational association"
                      value=""
                      onChange={(event) =>
                        actions.associate(event.currentTarget.value)
                      }
                    >
                      <option value="">Not linked — review required</option>
                      {associationOptions.map((service) => (
                        <option
                          key={service.serviceId}
                          value={service.serviceId}
                        >
                          {service.name}
                        </option>
                      ))}
                    </select>
                    <small className={panel.hint}>
                      Choose only after confirming these are the same clinic
                      service. Flio never matches names automatically.
                    </small>
                  </label>
                ) : null}

                <label className={panel.field}>
                  <span>NexHealth appointment type</span>
                  <select
                    aria-label="NexHealth appointment type"
                    aria-describedby={
                      appointmentType?.eligible === true
                        ? ids.typeHint
                        : ids.typeError
                    }
                    value={booking.appointmentTypeId}
                    onChange={(event) =>
                      actions.setAppointmentType(
                        Number(event.currentTarget.value)
                      )
                    }
                  >
                    {!appointmentType ? (
                      <option value={booking.appointmentTypeId} disabled>
                        Current appointment type unavailable
                      </option>
                    ) : null}
                    {appointmentTypes.map((item) => (
                      <option
                        key={item.id}
                        value={item.id}
                        disabled={!item.eligible}
                      >
                        {item.name} · {item.minutes} min
                        {!item.eligible ? ' · booking setup required' : ''}
                      </option>
                    ))}
                  </select>
                  {appointmentType?.eligible === true ? (
                    <small id={ids.typeHint} className={panel.hint}>
                      {appointmentType.minutes} min in NexHealth ·{' '}
                      {plural(
                        appointmentType.providerEvidence.length,
                        'provider',
                        'providers'
                      )}{' '}
                      ·{' '}
                      {plural(
                        appointmentType.operatoryEvidence.length,
                        'room',
                        'rooms'
                      )}
                    </small>
                  ) : (
                    <small
                      id={ids.typeError}
                      className={panel.fieldError}
                      role="alert"
                    >
                      <AlertTriangle size={14} aria-hidden="true" />
                      {appointmentType
                        ? `${label}: this appointment type exists in NexHealth but has no active provider and operatory hours association. It is not ready for Flio booking.`
                        : `${label}: the mapped appointment type is no longer available. Refresh provider resources or select a booking-ready type.`}
                    </small>
                  )}
                </label>

                <label className={panel.field}>
                  <span>Patient eligibility</span>
                  <select
                    aria-label="Patient eligibility"
                    value={booking.patientTypePolicy}
                    onChange={(event) =>
                      actions.setPatientType(
                        event.currentTarget.value as 'NEW' | 'EXISTING' | 'BOTH'
                      )
                    }
                  >
                    <option value="BOTH">New and existing</option>
                    <option value="NEW">New patients</option>
                    <option value="EXISTING">Existing patients</option>
                  </select>
                </label>

                <div className={styles.ageRule}>
                  <label className={styles.checkRow}>
                    <input
                      type="checkbox"
                      aria-label="Restrict by patient age"
                      checked={Boolean(booking.patientAgePolicy)}
                      onChange={(event) =>
                        actions.setAgeRestriction(event.currentTarget.checked)
                      }
                    />
                    <span>
                      <strong>Restrict by patient age</strong>
                      <small>
                        Show this booking option only within a defined age
                        range.
                      </small>
                    </span>
                  </label>
                  {booking.patientAgePolicy ? (
                    <div
                      className={styles.ageFields}
                      role="group"
                      aria-label="Eligible age range"
                    >
                      {(
                        [
                          ['minimumYears', 'Minimum age'],
                          ['maximumYears', 'Maximum age'],
                        ] as const
                      ).map(([bound, boundLabel]) => (
                        <label key={bound} className={panel.field}>
                          <span>{boundLabel}</span>
                          <span className={styles.yearsInput}>
                            <input
                              aria-label={boundLabel}
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={130}
                              aria-invalid={Boolean(errors.age)}
                              aria-describedby={
                                errors.age ? ids.ageError : undefined
                              }
                              value={booking.patientAgePolicy![bound]}
                              onChange={(event) =>
                                actions.setAgeBound(
                                  bound,
                                  event.currentTarget.value === ''
                                    ? ''
                                    : Number(event.currentTarget.value)
                                )
                              }
                            />
                            <span aria-hidden="true">years</span>
                          </span>
                        </label>
                      ))}
                      {errors.age ? (
                        <small
                          id={ids.ageError}
                          className={panel.fieldError}
                          role="alert"
                        >
                          {errors.age}
                        </small>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <label className={styles.checkRow}>
                  <input
                    type="checkbox"
                    aria-label="Emergency service"
                    checked={booking.emergencyService === true}
                    onChange={(event) =>
                      actions.setEmergency(event.currentTarget.checked)
                    }
                  />
                  <span>
                    <strong>Emergency service</strong>
                    <small>
                      The one service used for emergency visits. Its age and
                      availability rules still apply.
                    </small>
                    {emergencyReplaces.length > 0 ? (
                      <small className={styles.replaces}>
                        Replaces {emergencyReplaces.join(', ')} as the Emergency
                        service.
                      </small>
                    ) : null}
                  </span>
                </label>

                <details
                  className={styles.advanced}
                  // A format error must never hide behind the disclosure.
                  open={errors.group ? true : undefined}
                >
                  <summary>
                    <ChevronRight size={15} aria-hidden="true" />
                    <span>Variant group</span>
                    <small>{booking.patientFacingIntent ?? 'None'}</small>
                  </summary>
                  <label className={panel.field}>
                    <span>Variant group (optional)</span>
                    <input
                      aria-label="Variant group"
                      value={booking.patientFacingIntent ?? ''}
                      placeholder="e.g. CLEANING"
                      aria-invalid={Boolean(errors.group)}
                      aria-describedby={`${ids.groupHint}${errors.group ? ` ${ids.groupError}` : ''}`}
                      onChange={(event) =>
                        actions.setGroup(event.currentTarget.value)
                      }
                    />
                    <small id={ids.groupHint} className={panel.hint}>
                      Give variants of one service the same group, such as a
                      child and an adult cleaning. Patients ask for it once, and
                      Flio uses each variant’s age range to pick the right one.
                    </small>
                    {errors.group ? (
                      <small
                        id={ids.groupError}
                        className={panel.fieldError}
                        role="alert"
                      >
                        {errors.group}
                      </small>
                    ) : null}
                  </label>
                </details>
              </fieldset>
            ) : null}
          </div>
        )}

        <footer className={panel.dialogFooter}>
          {mode === 'remove' && removal ? (
            <>
              <p className={panel.dialogNote}>
                This can’t be undone. To offer it again, add it as a new
                service.
              </p>
              <div className={panel.footerActions}>
                <Button
                  ref={safeChoice}
                  type="button"
                  variant="secondary"
                  disabled={removing}
                  onClick={() => setMode('edit')}
                >
                  Keep service
                </Button>
                <Button
                  type="button"
                  className={panel.dangerSolid}
                  // Stays focusable while it works, so focus isn't lost.
                  aria-disabled={removing || undefined}
                  onClick={() => {
                    if (!removing) removal.onRemove()
                  }}
                >
                  {removing ? (
                    <>
                      <Loader2
                        size={15}
                        className={panel.spin}
                        aria-hidden="true"
                      />{' '}
                      Removing…
                    </>
                  ) : (
                    'Remove service'
                  )}
                </Button>
              </div>
            </>
          ) : mode === 'discard' ? (
            <>
              <p className={panel.dialogNote} role="alert">
                Discard your changes to this service?
              </p>
              <div className={panel.footerActions}>
                <Button
                  ref={safeChoice}
                  type="button"
                  variant="secondary"
                  onClick={() => setMode('edit')}
                >
                  Keep editing
                </Button>
                <Button type="button" onClick={onCancel}>
                  Discard changes
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className={`${panel.dialogNote} ${styles.applyNote}`}>
                Applied changes are saved for every service together with Save
                services.
              </p>
              {removal ? (
                <div className={styles.removeAction}>
                  <Button
                    type="button"
                    variant="secondary"
                    className={panel.dangerOutline}
                    disabled={Boolean(removal.blockedReason)}
                    aria-describedby={
                      removal.blockedReason
                        ? `${baseId}-remove-hint`
                        : undefined
                    }
                    onClick={() => ask('remove')}
                  >
                    <Trash2 size={15} aria-hidden="true" /> Remove service
                  </Button>
                  {removal.blockedReason ? (
                    <small id={`${baseId}-remove-hint`} className={panel.hint}>
                      {removal.blockedReason}
                    </small>
                  ) : null}
                </div>
              ) : null}
              <div className={panel.footerActions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={requestClose}
                >
                  Cancel
                </Button>
                <Button type="button" disabled={blocked} onClick={onApply}>
                  Apply
                </Button>
              </div>
            </>
          )}
        </footer>
      </section>
    </div>,
    document.body
  )
}
